import { datadogRum } from '@datadog/browser-rum';
import { AdminAction, AdminButton } from 'common/components/AdminButton';
import { IchiInsights } from 'common/models/Insights';
import { Treasury } from 'common/models/treasury';
import { navigate } from 'gatsby';
import useParsedQueryString from 'hooks/useParsedQueryString';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import { TokenUtils } from 'utils/tokenUtils';
import { MintEvent } from 'xstate/mintEvent';
import {
  EventInputChangeArgs,
  EventSelectTokenToMintArgs,
  EventUpdateAccountInfoArgs,
  initialMintContext,
  mintService,
  MintState,
} from 'xstate/mintMachine';
import { XStateUtils } from 'xstate/xStateUtils';
// import { InsufficientMemberTokenError } from '../Errors/InsufficientMemberTokenError';
import GeneralInput from '../GeneralInput/GeneralInput';
import { InputError } from '../GeneralInput/InputError';
import SelectToken from '../SelectToken/SelectToken';
import SpinnerButton from '../SpinnerButton';
import MintMetadata from './MintMetadata';
import { Nullable } from 'common/models/optional';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { MINTABLE_TOKENS, MintableToken } from 'constants/contracts';
import { chains } from 'containers/WalletProvider/WalletProvider';

type Props = {
  treasuries: Treasury[];
  isLoadingTreasuries: boolean;
};

const Mint: React.FC<Props> = (props) => {
  const { name: urlName } = useParsedQueryString();
  const { chainId } = useSelectedChain();
  const chain = chains.find((c) => c.id === chainId);
  const { address: accountAddress } = useAccount();
  const [mintableTokens, setMintableTokens] = useState(
    Object.values(MINTABLE_TOKENS[chainId] || [])
  );

  useEffect(() => {
    setMintableTokens(Object.values(MINTABLE_TOKENS[chainId] || []));
  }, [chainId]);

  // mintService
  const [amount, setAmount] = React.useState(initialMintContext.amount);
  const [tokenToMint, setTokenToMint] = React.useState(
    initialMintContext.tokenToMint
  );
  const [inputError, setInputError] = React.useState<string>();
  const [tradeUrl, setTradeUrl] = React.useState<Nullable<string>>();
  const [isLoadingData, setIsLoadingData] = React.useState(
    initialMintContext.isLoadingData
  );
  const [dataResponse, setDataResponse] = React.useState(
    initialMintContext.dataResponse
  );
  const [isMinting, setIsMinting] = React.useState(
    initialMintContext.isMinting
  );
  const [isMintingDisabled, setIsMintingDisabled] = React.useState(
    initialMintContext.isMintingDisabled
  );
  const [isMemberApproved, setIsMemberApproved] = React.useState(
    initialMintContext.isMemberApproved
  );
  const [isApprovingMember, setIsApprovingMember] = React.useState<boolean>(
    initialMintContext.isApprovingMember
  );
  const [isResettingMember, setIsResettingMember] = React.useState(
    initialMintContext.isResettingMember
  );
  const [isCollateralApproved, setIsCollateralApproved] = React.useState(
    initialMintContext.isCollateralApproved
  );
  const [isApprovingCollateral, setIsApprovingCollateral] = React.useState(
    initialMintContext.isApprovingCollateral
  );
  const [isResettingCollateral, setIsResettingCollateral] = React.useState(
    initialMintContext.isResettingCollateral
  );
  const [isInsufficientMember, setIsInsufficientMember] = React.useState(false);

  React.useEffect(() => {
    const subscription = mintService.subscribe((state) => {
      if (state.context.amount !== amount) {
        setAmount(state.context.amount);
      }

      if (state.context.tokenToMint !== tokenToMint) {
        setTokenToMint(state.context.tokenToMint);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.tradeUrl != tradeUrl) {
        setTradeUrl(state.context.tradeUrl);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.dataResponse !== dataResponse) {
        setDataResponse(state.context.dataResponse);
      }

      if (state.context.isMinting !== isMinting) {
        setIsMinting(state.context.isMinting);
      }

      if (state.context.isMintingDisabled !== isMintingDisabled) {
        setIsMintingDisabled(state.context.isMintingDisabled);
      }

      if (state.context.isMemberApproved !== isMemberApproved) {
        setIsMemberApproved(state.context.isMemberApproved);
      }

      if (state.context.isApprovingMember !== isApprovingMember) {
        setIsApprovingMember(state.context.isApprovingMember);
      }

      if (state.context.isCollateralApproved !== isCollateralApproved) {
        setIsCollateralApproved(state.context.isCollateralApproved);
      }

      if (state.context.isApprovingCollateral !== isApprovingCollateral) {
        setIsApprovingCollateral(state.context.isApprovingCollateral);
      }

      if (state.context.isResettingMember !== isResettingMember) {
        setIsResettingMember(state.context.isResettingMember);
      }

      if (state.context.isResettingCollateral !== isResettingCollateral) {
        setIsResettingCollateral(state.context.isResettingCollateral);
      }

      if (
        state.matches(MintState.INSUFFICIENT_MEMBER) &&
        !isInsufficientMember
      ) {
        setIsInsufficientMember(true);
      } else if (isInsufficientMember) {
        setIsInsufficientMember(false);
      }
    });

    // mintService.onEvent((e) => {
    //   if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
    //     props.refetchUserInfo();
    //   }
    // });

    return subscription.unsubscribe;
  }, [
    mintService,
    amount,
    tokenToMint,
    inputError,
    tradeUrl,
    isLoadingData,
    dataResponse,
    isMinting,
    isMintingDisabled,
    isMemberApproved,
    isApprovingMember,
    isCollateralApproved,
    isApprovingCollateral,
    isResettingMember,
    isResettingCollateral,
  ]);

  React.useEffect(() => {
    if (chainId && accountAddress) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      mintService.send(
        MintEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress]);

  const onChangeAmount = (inputValue: string) => {
    const args: EventInputChangeArgs = {
      inputValue,
    };
    mintService.send(
      MintEvent.INPUT_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  };

  const onSelectOneToken = (c?: MintableToken, logAction = true) => {
    if (!c || !c.token.symbol) {
      console.warn('Could not select token, no token given!');
      return;
    }

    if (c.token.symbol) {
      if (logAction === true) {
        datadogRum.addAction(
          IchiInsights.DataDogActions.Mint.SelectToken(c.token.symbol)
        );
      }

      const treasury = TokenUtils.getTreasuryByToken(
        props.treasuries,
        c.token.symbol
      );
      if (treasury) {
        const payload: EventSelectTokenToMintArgs = {
          tokenToMint: c.token.address,
          tradeUrl: treasury.tradeUrl,
          mintingRatio: treasury.mintingRatio,
          mintingFee: treasury.mintFee,
        };
        mintService.send(
          MintEvent.SELECT_TOKEN_TO_MINT,
          XStateUtils.createEventPayload(payload)
        );

        // Only navigate if the urls don't match
        if (urlName !== c.token?.symbol && c?.token?.symbol) {
          const urlQuery = qs.stringify({
            name: c.token.symbol,
            collateral: c.collateralToken?.symbol,
          });
          void navigate(`/mint?${urlQuery}`);
        }
      }
    }
  };

  // Handle when the urlName changes or mintableTokens changes
  // In other words when the user loads the page
  React.useEffect(() => {
    const urlToken = mintableTokens.find((c) => c.token.symbol === urlName);

    if (urlName !== urlToken?.token?.symbol && urlToken?.token?.symbol) {
      // Handle updating the url with the collateral token, since there is only one right now, we only do this once.
      const urlQuery = qs.stringify({
        name: urlToken.token.symbol,
        collateral: urlToken.collateralToken?.symbol,
      });
      void navigate(`/mint?${urlQuery}`);
    }
    // Make sure that we only select a token if it already exists
    else if (urlToken) {
      onSelectOneToken(urlToken, false);
    }
    // In this edge case we may have switched networks and the url doesn't match, we have no token, but we do have mintable tokens
    // so let's select the first one
    else if (!urlToken && !!mintableTokens && mintableTokens.length > 0) {
      const token = mintableTokens[0];
      const urlQuery = qs.stringify({
        name: token.token.symbol,
        collateral: token.collateralToken?.symbol,
      });
      void navigate(`/mint?${urlQuery}`);
    }
  }, [mintableTokens, urlName, props.treasuries]);

  const selectedOneToken = (mintableTokens || []).find(
    (t) => t.token.address === tokenToMint
  );

  const collateralTokens: MintableToken[] = selectedOneToken
    ? [selectedOneToken]
    : [];
  const isMintMetadataDisplayed = amount ? parseInt(amount, 10) > 0 : false;
  const adminActions: AdminAction[] = [
    {
      text: 'Reset Member Allowance',
      onClick: () => mintService.send(MintEvent.RESET_MEMBER_ALLOWANCE),
    },
    {
      text: 'Approve Member Allowance',
      onClick: () => mintService.send(MintEvent.APPROVE_MEMBER_ALLOWANCE),
    },
    {
      text: 'Reset Collateral Allowance',
      onClick: () => mintService.send(MintEvent.RESET_COLLATERAL_ALLOWANCE),
    },
    {
      text: 'Approve Collateral Allowance',
      onClick: () => mintService.send(MintEvent.APPROVE_COLLATERAL_ALLOWANCE),
    },
  ];

  // Even when a wallet is not connected we still have a list of treasuries to display, so assume the mintingRatio
  // from that treasury found in the URL
  const treasuryFromUrl = TokenUtils.getTreasuryByToken(
    props.treasuries,
    urlName as string
  );

  const renderMintingActionsButton = () => {
    const isMintButtonLoading =
      isLoadingData ||
      isApprovingMember ||
      isApprovingCollateral ||
      isMinting ||
      isResettingCollateral ||
      isResettingMember;
    let isMintButtonDisabled =
      isMintButtonLoading || isMintingDisabled || !amount || amount === '0';
    let clickAction = () => mintService.send(MintEvent.MINT);
    let mintButtonLabel = 'Mint';
    if (isMemberApproved === false) {
      clickAction = () => mintService.send(MintEvent.APPROVE_MEMBER_ALLOWANCE);
      mintButtonLabel = `Approve ${selectedOneToken?.memberToken?.name}`;
      isMintButtonDisabled = isMintButtonDisabled || !isMintMetadataDisplayed;
    } else if (isCollateralApproved === false) {
      clickAction = () =>
        mintService.send(MintEvent.APPROVE_COLLATERAL_ALLOWANCE);
      mintButtonLabel = `Approve ${selectedOneToken?.collateralToken?.name}`;
      isMintButtonDisabled = isMintButtonDisabled || !isMintMetadataDisplayed;
    }
    return (
      <SpinnerButton
        className="primary-button-bg-color"
        isLoading={isMintButtonLoading}
        disabled={isMintButtonDisabled}
        onClick={clickAction}
        data-dd-action-name={`${mintButtonLabel} ${selectedOneToken?.token.symbol}`}
      >
        {mintButtonLabel}
      </SpinnerButton>
    );
  };

  return (
    <div className="flex flex-col mt-2 heading-color">
      <div className="p-3">
        <div className="grid w-full">
          <SelectToken
            name="one-token"
            values={mintableTokens}
            isLoading={props.isLoadingTreasuries}
            value={selectedOneToken as MintableToken}
            onChange={onSelectOneToken}
            id="oneToken"
          />
          <GeneralInput
            isNumber={true}
            label="Enter Amount"
            step="1"
            value={amount}
            onChange={onChangeAmount}
          />
          <InputError
            inputError={inputError}
            isLinkable={isInsufficientMember}
            tradeUrl={tradeUrl}
          />
        </div>
        <div className="flex flex-row justify-between items-center w-full mt-2">
          <div className="value-text">Collateral</div>
          <SelectToken
            values={collateralTokens}
            isLoading={props.isLoadingTreasuries}
            isCollateral={true}
            name="collateral-token"
            value={selectedOneToken as MintableToken}
            onChange={onSelectOneToken}
            id="collateral"
          />
        </div>
      </div>
      <MintMetadata
        display={isMintMetadataDisplayed}
        amount={amount}
        treasury={treasuryFromUrl}
        tokenContractContainer={selectedOneToken as MintableToken}
      />
      <div className="footer-bg-color flex justify-center items-center h-24 box-footer">
        {renderMintingActionsButton()}
        {chain?.testnet && <AdminButton actions={adminActions} />}
      </div>
    </div>
  );
};

export default Mint;
